import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FlaskConical, ShieldAlert, Phone, MapPin, Building2, Calendar,
  Loader2, FileText, AlertTriangle,
} from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import NavCluster from '../components/NavCluster';

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

export default function PublicSDSView() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/sds/${id}`);
        setDoc(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'تعذر تحميل التقرير');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" dir="rtl">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ModonLogo size={44}/>
            <div>
              <h1 className="font-extrabold text-slate-900">عرض تقرير SDS</h1>
              <p className="text-xs text-slate-500">View Safety Data Sheet (Public)</p>
            </div>
          </div>
          <NavCluster homePath="/" testidPrefix="public-sds" />
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-600"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
        ) : err ? (
          <Card className="p-8 text-center text-slate-600"><AlertTriangle className="mx-auto mb-2 text-amber-500" size={32}/> {err}</Card>
        ) : doc && (
          <div className="space-y-4">
            {/* Header card */}
            <Card className="p-5">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <h2 className="text-2xl font-extrabold text-slate-900" data-testid="public-sds-title">{doc.title || 'تقرير SDS'}</h2>
                  <div className="text-slate-600 flex items-center gap-2 text-sm">
                    <Building2 size={14}/> {doc.factoryName || '—'}
                  </div>
                  <div className="text-slate-600 flex items-center gap-2 text-sm">
                    <MapPin size={14}/> {doc.factoryCity || '—'}{doc.factoryAddress ? ' • ' + doc.factoryAddress : ''}
                  </div>
                  {doc.factoryPhone && (
                    <div className="text-slate-600 flex items-center gap-2 text-sm">
                      <Phone size={14}/> {doc.factoryPhone}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    {doc.risk && (
                      <Badge style={{ backgroundColor: RISK_COLORS[doc.risk.level] || '#64748b', color: 'white' }} data-testid="public-sds-risk">
                        <ShieldAlert size={11} className="ml-1"/> {doc.risk.labelAr} ({doc.risk.score})
                      </Badge>
                    )}
                    <span className="text-xs text-slate-500"><Calendar size={11} className="inline ml-1"/> آخر تحديث: {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString('ar-SA') : '—'}</span>
                  </div>
                </div>
                {/* QR */}
                <div className="bg-white p-2 rounded border" data-testid="public-sds-qr">
                  <QRCodeSVG value={pageUrl} size={110} bgColor="#ffffff" fgColor="#1a4d80"/>
                  <div className="text-[10px] text-center mt-1 text-slate-500">QR للتقرير</div>
                </div>
              </div>
            </Card>

            {/* Chemicals */}
            <Card className="p-5">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-1"><FlaskConical size={16}/> المواد الكيميائية ({(doc.data?.chemicals || []).length})</h3>
              {(doc.data?.chemicals || []).length === 0 ? (
                <div className="text-slate-500 text-sm">لا توجد مواد مسجلة</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600">
                      <tr>
                        <th className="text-right p-2">#</th>
                        <th className="text-right p-2">المادة</th>
                        <th className="text-right p-2">رقم UN</th>
                        <th className="text-right p-2">تصنيف GHS</th>
                        <th className="text-right p-2">NFPA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(doc.data?.chemicals || []).map((c, i) => (
                        <tr key={i} className="border-t" data-testid={`public-sds-chem-${i}`}>
                          <td className="p-2 font-bold">{i + 1}</td>
                          <td className="p-2 font-bold text-slate-900">{c.name || '—'}<div className="text-[10px] text-slate-500">{c.chemicalName || ''}</div></td>
                          <td className="p-2 text-slate-700">{c.unNumber || c.un || '—'}</td>
                          <td className="p-2 text-slate-700 text-xs">{c.ghsClassification || '—'}</td>
                          <td className="p-2 text-xs">
                            {c.nfpa ? (
                              <span>H:{c.nfpa.health ?? 0} F:{c.nfpa.fire ?? 0} R:{c.nfpa.reactivity ?? 0}{c.nfpa.special && c.nfpa.special !== '-' ? ` ${c.nfpa.special}` : ''}</span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
      <SiteFooter/>
    </div>
  );
}
